import { forwardRef, useCallback, useImperativeHandle, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { isEmpty } from 'lodash';
import PropTypes from 'prop-types';
import { toSentence, trackError } from 'utils/helpers';
import LoadingButton from 'components/loading_button';
import { fetchCurrent } from 'redux/actions/booking';
import {
  deleteReservation,
  removeAllFerryAndAssociatedVehicles,
} from 'redux/actions/reservation';
import { getCurrentBooking } from 'redux/selectors/current_booking';
import { buttonText, getModalContent } from './helpers';
import { useReservationRemoval } from './use_reservation_removal';

const ReplaceReservationWarning = forwardRef(
  ({ visible, scene, onClose, onRemove, product }, ref) => {
    const dispatch = useDispatch();
    const currentBooking = useSelector(getCurrentBooking);
    const [isRemoving, setIsRemoving] = useState(false);

    const {
      reservationTobeDeleted,
      reservationsExcludeFerry,
      reservationTypes,
    } = useReservationRemoval(scene, currentBooking, product);

    const hasSomethingToRemove = reservationTobeDeleted.length !== 0;

    // It is for parent component
    useImperativeHandle(
      ref,
      () => ({
        hasSomethingToRemove: () => hasSomethingToRemove,
      }),
      [hasSomethingToRemove]
    );

    const removeHandler = useCallback(async () => {
      try {
        setIsRemoving(true);
        // Experience page is different with others on removing reservations
        if (scene === 'experience') {
          // Remove entire ferry from current booking
          if (reservationTypes.includes('ferry trip')) {
            await dispatch(removeAllFerryAndAssociatedVehicles());
          }

          // Remove accommadation and experience reservations from the current booking
          if (!isEmpty(reservationsExcludeFerry)) {
            const tasks = reservationsExcludeFerry.map(({ id }) =>
              dispatch(deleteReservation(currentBooking.id, id, false))
            );
            await Promise.all(tasks);
          }
        } else {
          const tasks = reservationTobeDeleted.map(({ id }) =>
            dispatch(deleteReservation(currentBooking.id, id, false))
          );
          await Promise.all(tasks);
        }
        await dispatch(fetchCurrent());
        setIsRemoving(false);
        onRemove?.call();
      } catch (error) {
        trackError(error);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
      scene,
      reservationTypes,
      onRemove,
      currentBooking,
      reservationsExcludeFerry,
      reservationTobeDeleted,
    ]);

    const closeHandler = () => {
      onClose?.call();
    };

    return (
      <Modal
        // Sometimes the modal's visibility is controlled by father component,
        // for example, the one in accommodation
        show={visible === undefined ? hasSomethingToRemove : visible}
        onHide={closeHandler}
        centered
        backdrop="static"
        backdropClassName="cover-off-canvas"
      >
        <Modal.Header closeButton={buttonText[scene].canClose && !isRemoving}>
          <Modal.Title>Important message</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center p-3">
          {getModalContent(
            scene,
            reservationTobeDeleted,
            currentBooking?.reference,
            reservationTypes
          )}
        </Modal.Body>
        <Modal.Footer className="border-0 pb-4">
          <div className="w-100 p-0">
            <div className="d-flex w-100">
              <button
                type="button"
                onClick={closeHandler}
                aria-label="cancel removing reservation"
                className="btn border-grey text-grey w-auto me-2 text-nowrap"
                data-ga-events="ga_events_keep_booking_contents"
                disabled={isRemoving}
              >
                {buttonText[scene].cancel}
              </button>
              <LoadingButton
                loading={isRemoving}
                disabled={isRemoving}
                className="btn btn-danger w-100"
                onClick={removeHandler}
                data-ga-events="ga_events_discard_item_and_continue"
              >
                {isRemoving
                  ? 'Removing reservations...'
                  : buttonText[scene].confirm ||
                    `Remove ${toSentence(reservationTypes)} and proceed`}
              </LoadingButton>
            </div>
          </div>
        </Modal.Footer>
      </Modal>
    );
  }
);

ReplaceReservationWarning.propTypes = {
  visible: PropTypes.bool,
  onClose: PropTypes.func,
  onRemove: PropTypes.func,
  scene: PropTypes.oneOf(['accommodation', 'ferry', 'experience']).isRequired,
  product: PropTypes.shape({
    hasFerry: PropTypes.bool.isRequired,
    hasAccommodation: PropTypes.bool.isRequired,
  }),
};

export default ReplaceReservationWarning;
